import { EmptyObject, Entity, EntityArrayDiff, ID, UpdateData } from '../types/UtilityTypes'
import { isEntity, isEntityArray, isSimpleType } from './type-guards'
import * as A from 'fp-ts/es6/Array'
import { getOrElse } from 'fp-ts/es6/Option'
import { not, Predicate, pipe } from 'fp-ts/es6/function'
import { diffArray, isDefinedRecord, omit, replace, snoc, toggle, unbox, unboxRecord } from './utils'
import { fromEquals } from 'fp-ts/es6/Eq'
import { contramap, fromCompare, ordNumber, ordString } from 'fp-ts/es6/Ord'

export const isDefinedEntity = <T extends Entity>(entity: T): boolean => pipe(
    entity,
    omit('id'),
    isDefinedRecord,
)

export const getId = (entity: Entity): ID => entity.id


export function simpleDiff<T extends string | number>(a?: T, b?: T): T | undefined {
    return b !== undefined && a !== b ? b : undefined
}

export const diffEntity = <T extends Entity>(object?: T | null, changes?: T | null): T | undefined | null => {
    if (object?.id && changes === null) {
        return null
    }
    const result = changes?.id !== object?.id ? changes : undefined
    if (result?.id === 0) {
        return undefined
    }
    return result
}

export const eqEntityOpt = fromEquals<Entity | null | undefined>((x, y) => x?.id === y?.id)
export const eqId = fromEquals<ID>((x, y) => x == y)

export const eqEntity = fromEquals<Entity>((x, y) => x.id == y.id)
export const eqArray = A.getEq(eqEntity)
export const ordId = fromCompare<ID>((a, b) => {
    if (typeof a === 'number' && typeof b === 'number') {
        return ordNumber.compare(a, b)
    }
    if (typeof a === 'string' && typeof b === 'string') {
        return ordString.compare(a, b)
    }
    if (a === 'number' && typeof b === 'string') {
        return -1
    }
    if (a === 'string' && typeof b === 'number') {
        return 1
    }
    return 0
})
export const ordEntity = contramap((entity: Entity) => entity.id)(ordId)
export const arraySort = A.sort(ordEntity)
export const entityElem = A.elem(eqEntityOpt)

export const getExecutorsId = (item:any)=> { // eslint-disable-line
    let result: any // eslint-disable-line
    item.work_type != undefined && item.user != undefined ?
        result = {
            id: item.id > 0 ? item.id : null,
            work_type_id:item.work_type.id,
            user_id:item.user.id
        } :
        result = undefined
    return result
}

export const diffEntityArray = <T extends Entity>(array: T[] = [], changes: T[] = []): EntityArrayDiff | undefined => {
    const diff = diffArray(ordEntity)(array, changes)
    return unboxRecord({
        add: unbox(diff.add.map(getId)),
        delete: unbox(diff.delete.map(getId)),
    })
}
export const diffEntityNewExecutorsArray = <T extends Entity>(array: T[] = []): EntityArrayDiff | undefined => {
    return {
        add: array.map(item => getExecutorsId(item))
    }
}
export const diffEntityExecutorsArray = <T extends Entity>(array: T[] = [], changes: T[] = []): EntityArrayDiff | undefined => {
    const diff = diffArray(ordEntity)(array, changes)
    return unboxRecord({
        add: changes.map(item => getExecutorsId(item)),
        delete: unbox(diff.delete.map(getId)),
    })
}

export const diffEntityArraySimple = <T extends Entity>(array: T[] = [], changes: T[] = []): T[] | undefined => {
    if (!eqArray.equals(arraySort(array), arraySort(changes))) {
        return changes
    }
}

export const prepareForServer = <T extends EmptyObject>(x: T): UpdateData<T> => {
    return Object.entries(x).reduce((acc, [key, value]) => {
        if (key === 'id') {
            return acc
        }
        // todo check why it exist
        // if (typeof value === 'string' && value.length === 0) {
        //     acc[key] = null
        //     return acc
        // }
        if (key === 'executors') {
            if(value != undefined) {
                if(typeof value !== 'object') {
                    acc[key] = {
                        add: value.map(function (item:any) { // eslint-disable-line
                            return {
                                work_type_id: item.work_type.id,
                                user_id: item.user.id
                            }
                        })
                    }
                } else {
                    acc[key] = value
                }
                return acc
            }

        }
        if (isEntity(value)) {
            acc[key] = value.id || undefined // if id === 0
            return acc
        }
        if (isEntityArray(value)) {
            acc[key] = {
                add: value.map(item => item.id)
            }
            return acc
        }
        acc[key] = value
        return acc
    }, {} as Record<string, unknown>) as UpdateData<T>
}

export const diffAndPrepare = <T extends EmptyObject>(object: T, changes: Partial<T>): UpdateData<T> => {
    return Object.entries(changes).reduce((acc, [key, value]) => {
        const prevValue = (object as Record<string, unknown>)[key]
        if ((isEntity(prevValue) || prevValue === null) && (isEntity(value) || value === null)) {
            const diff = diffEntity(prevValue, value)
            if (diff === null) {
                acc[key] = null
            } else {
                acc[key] = diff?.id
            }
            return acc
        }
        if ((isEntityArray(prevValue) || prevValue === undefined) && isEntityArray(value)) {
            acc[key] = diffEntityArray(prevValue || [], value)
            return acc
        }
        if (isSimpleType(prevValue) && isSimpleType(value)) {
            acc[key] = simpleDiff(prevValue, value)
            return acc
        }
        acc[key] = undefined
        return acc
    }, {} as Record<string, unknown>) as UpdateData<T>
}

export const entityEqPredicate = <T extends Entity>(entity?: T | null) => (entity2: T): boolean => entity ? entity.id == entity2.id : false

export const entityIdPredicate = <T extends Entity>(id?: ID) => (entity: T): boolean => entity.id == id

export const entityToggle = <T extends Entity>(entity: T) => (array: T[]): T[] => {
    const exist = entityElem(entity, array)
    return (exist ? entityRemove : snoc)(entity)(array)
}

export const entityReplace = <T extends Entity>(entity: T) => (array: T[]): T[] =>
    array.map(item => eqEntity.equals(item, entity) ? { ...item, ...entity } : item)

export const idReplace = replace(eqId)

export const idRemove = (value: ID) => (array: ID[]): ID[] => array.filter(item => item != value)

export const entityArrayReplace = <T extends Entity>(source: T[]) => (origin: T[]): T[] =>
    origin.map(item => pipe(
        A.findFirst<T>(entityEqPredicate(item))(source),
        getOrElse(() => item)
    ))

export const entityArrayRemove = <T extends Entity>(entities: T[]) => (target: T[]): T[] =>
    pipe(
        target,
        A.filter(item => !entityElem(item, entities))
    )

export const entityRemove = <T extends Entity>(entity: T) => (array: T[]): T[] =>
    array.filter(not(entityEqPredicate(entity)))


export const entityModify = <T extends Entity>(predicate: Predicate<T>, modifier: (entity: T) => T) => (array: T[]): T[] =>
    array.map(entity => predicate(entity) ? modifier(entity) : entity)


export const isNew = <T extends Entity>(entity: T): boolean => typeof entity.id === 'number' && entity.id < 0
export const isOld = not(isNew)

export const updatedEntities = <T extends Entity>(a: T[] = [], b: T[] = []): (Entity & UpdateData<T>)[] => pipe(
    A.zipWith(
        A.intersection<T>(eqEntity)(a, b),
        A.intersection<T>(eqEntity)(b, a),
        (old, changes) => ({
            ...diffAndPrepare(old, changes),
            id: old.id,
        }),
    ),
    A.filter(isDefinedEntity)
)

export const conditionToggle = <T extends Entity>(condition: boolean, a: T): (as: T[])=> T[] => (condition? snoc: entityRemove)(a)

export const idToggle = toggle(eqId)
// export const idMerge = (ys: ID[]) => (xs: ID[]): ID[] => A.union(eqId)(xs, ys)
// export const entityArrayRemove = <T extends Entity>(entities: T[]) => (array: T[]): T[] =>
//     A.difference<T>(eqEntity)(array, entities)
// export const entityRemoveById = <T extends Entity>(id: ID) => (array: T[]): T[] => array.filter((entity) => entity.id !== id)
// export const entityReplaceNew = <T extends Entity>(entity: T, newEntity: T) => (array: T[]): T[] =>
//     array.map(item => eqEntity.equals(item, entity) ? newEntity : item)
